// The checkout acknowledgement gate (Design round 3, specimen-reconciled in
// round 3.1): five affirmative tap-to-check acknowledgements between the
// coverage page and payment. The payment button is unreachable until all
// five are checked (disabled label verbatim from the handoff). Wording comes
// from the SERVER, fetched per quote (the type ack names this quote's
// selected aircraft types); this component carries none of its own. Each
// ack's "read more" shows the verbatim specimen clause and cites form +
// edition. When the server's gate mode is off this component is never
// rendered.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api.js';

export default function CheckoutGate({ quoteToken, cfiExtension, onComplete, onBack }) {
  const [acks, setAcks] = useState(null); // null = loading, [] = fetch failed
  const [checked, setChecked] = useState({});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fetchTry, setFetchTry] = useState(0);
  const allChecked = Array.isArray(acks) && acks.length > 0 && acks.every((a) => checked[a.key]);

  useEffect(() => {
    let live = true;
    setAcks(null);
    api.get(`/api/ctr/quotes/${encodeURIComponent(quoteToken)}/acks?cfi=${cfiExtension ? 1 : 0}`)
      .then((r) => { if (live) setAcks(r.mode === 'on' && Array.isArray(r.acks) ? r.acks : []); })
      .catch(() => { if (live) setAcks([]); });
    return () => { live = false; };
  }, [quoteToken, cfiExtension, fetchTry]);

  async function proceed() {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/ctr/quotes/${encodeURIComponent(quoteToken)}/acknowledge`, {
        keys: acks.map((a) => a.key),
        cfi_extension: cfiExtension,
      });
      onComplete();
    } catch {
      setError('We could not record your acknowledgements just now. Nothing is lost; try again.');
    } finally { setBusy(false); }
  }

  return (
    <>
      <h1 className="mb-1.5 mt-1.5 text-[23px] font-bold leading-[1.2] tracking-[-0.02em]">Five things to acknowledge</h1>
      <p className="mb-4 text-sm leading-[1.55] text-ink-dim">
        These are the rules of the policy itself, in plain language. Tap each one to confirm you understand it.{' '}
        <a href="/policy-diff" target="_blank" rel="noreferrer" className="font-semibold text-accent">Full plain-English summary</a>
      </p>
      {acks === null ? (
        <div className="mb-5 rounded-[11px] border-[1.5px] border-line bg-white p-4 text-[13px] text-ink-dim">Loading the acknowledgements…</div>
      ) : acks.length === 0 ? (
        <div className="mb-5 rounded-[11px] border-[1.5px] border-line bg-white p-4 text-[13px] text-ink-dim">
          We could not load the acknowledgements just now.{' '}
          <button type="button" onClick={() => setFetchTry((n) => n + 1)} className="font-semibold text-accent">Try again</button>
        </div>
      ) : (
        <div className="mb-5 flex flex-col gap-2">
          {acks.map((a) => {
            const on = !!checked[a.key];
            const showing = open === a.key;
            return (
              <div key={a.key} className={`rounded-[11px] border-[1.5px] ${on ? 'border-accent bg-teal-card' : 'border-line bg-white'}`}>
                <button type="button" onClick={() => setChecked((c) => ({ ...c, [a.key]: !c[a.key] }))}
                  className="flex w-full items-start gap-[11px] p-[13px_14px] text-left">
                  <span className={`mt-[1px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] border-[1.5px] text-[12px] font-bold text-white ${on ? 'border-accent bg-accent' : 'border-line-strong bg-white'}`}>{on ? '✓' : ''}</span>
                  <span className="text-[13px] leading-[1.55] text-ink-soft">{a.wording}</span>
                </button>
                <div className="px-[14px] pb-[11px] pl-[43px]">
                  <button type="button" onClick={() => setOpen(showing ? null : a.key)}
                    className="text-[11.5px] font-semibold text-accent">
                    {showing ? 'Hide the policy language' : 'Read the policy language'}
                  </button>
                  {showing ? (
                    <div className="mt-1.5 rounded-lg bg-fill p-[9px_12px]">
                      <div className="text-[12px] italic leading-[1.55] text-ink-soft">&ldquo;{a.clause}&rdquo;</div>
                      <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-ink-faint">{a.citation}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <button type="button" disabled={!allChecked || busy} onClick={proceed}
        className="min-h-[52px] w-full rounded-card-lg bg-accent text-base font-bold text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-fill disabled:text-ink-dim">
        {allChecked ? (busy ? 'Recording…' : 'Continue to payment →') : 'Check all five to continue'}
      </button>
      {error ? <div className="mt-2 text-center text-[12.5px] font-semibold text-warn-fg">{error}</div> : null}
      <button type="button" onClick={onBack} className="mt-1.5 min-h-[44px] w-full text-[13.5px] font-semibold text-ink-dim">← Back to my coverage</button>
    </>
  );
}
